import type { ReactNode } from 'react'

import { AlertTriangle } from 'lucide-react'

import emptyTray from '@/assets/illustrations/empty-tray.svg'
import { BrandIcon } from '@/components/BrandIcon'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'

/// Ожидание: пульсирует сам знак приложения, а не абстрактное колесо. На
/// длинной выборке видно, что панель жива и занята делом.
export function LoadingState({ message, className }: { message?: string; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-4 p-12 text-ink-muted', className)}>
      <BrandIcon size={44} radius={14} className="animate-breathe" />
      <p className="text-sm">{message ?? 'Загружаем данные…'}</p>
    </div>
  )
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  className,
}: {
  title: string
  description?: ReactNode
  /** Своя картинка вместо лотка — например, когда пусто по фильтру, а не вообще. */
  icon?: ReactNode
  action?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn('flex animate-fade-up flex-col items-center justify-center gap-3 p-12 text-center', className)}
    >
      {icon ?? <img src={emptyTray} alt="" width={176} height={128} className="opacity-95" />}
      <div>
        <p className="text-base font-semibold text-ink">{title}</p>
        {description ? <p className="mt-1 max-w-md text-sm text-ink-muted">{description}</p> : null}
      </div>
      {action ? <div className="mt-1">{action}</div> : null}
    </div>
  )
}

export function ErrorState({
  message,
  onRetry,
  className,
}: {
  message: string
  onRetry?: () => void
  className?: string
}) {
  return (
    <div
      className={cn('flex animate-fade-up flex-col items-center justify-center gap-3 p-12 text-center', className)}
    >
      <span className="flex size-14 items-center justify-center rounded-2xl bg-danger/10 text-danger">
        <AlertTriangle size={28} />
      </span>
      <p className="max-w-lg text-sm whitespace-pre-line text-ink">{message}</p>
      {onRetry ? (
        <Button variant="outlined" size="sm" onClick={onRetry}>
          Повторить
        </Button>
      ) : null}
    </div>
  )
}

/// Плашка с ошибкой внутри формы — как красный контейнер в LoginScreen.
export function ErrorBanner({ message, className }: { message: string; className?: string }) {
  return (
    <div
      className={cn(
        'animate-fade-up rounded-xl bg-danger/10 px-3.5 py-3 text-sm whitespace-pre-line text-danger',
        className,
      )}
    >
      {message}
    </div>
  )
}
